import * as React from "react";
import { observer } from "mobx-react";
import { UitkCard, UitkCardMedia, UitkCardLink, UitkCardTextSegment } from "uitk-react-cards";
import { UitkImage, UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { UitkBadge } from "uitk-react-badge";
import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { locKeys } from "components/shared/Merchandising/Inventory/l10n";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkPriceLockup } from "uitk-react-price-lockup";
import { withStores } from "stores";
import { RouteStore, QueryRoute } from "bernie-core";
import { Link } from "react-router-dom";
import { Layer } from "bernie-view";
import { InfoDialog } from "../../Common/InfoDialog/InfoDialog";
import { MabTrackEvent, withMabTracker } from "src/components/utility/analytics/MabTracker";

export interface HotelInventoryCardProps {
  offer: Offers;
  cardIndex: number;
  campaignIndex: number;
  openDialog: (dialogPath: string) => void;
  closeDialog: () => void;
  moduleName: string;
  withBorder?: boolean;
  routeStore: RouteStore;
  mabTrack?: (x: MabTrackEvent) => null;
}

export const HotelInventoryCard = withMabTracker(
  withStores("routeStore")(
    observer(
      ({
        offer,
        cardIndex,
        campaignIndex,
        closeDialog,
        moduleName,
        withBorder,
        routeStore,
        mabTrack,
      }: HotelInventoryCardProps) => {
        if (offer.__typename !== "HotelOfferItem") {
          return null;
        }
        const { formatText } = useLocalization();
        const strikeThroughPrice = offer?.lodgingOffer?.strikeThroughPrice?.value;
        const currentPrice = offer.lodgingOffer?.price.value;
        const a11yPrice = formatText(locKeys.a11yPriceLockupLabel, strikeThroughPrice, currentPrice);

        const priceDisclaimerLabel = formatText(locKeys.priceDisclaimerLabel);

        const disclaimerInfoDialog = () => (
          <QueryRoute query={{ pwaDialog: `disclaimer-info-${campaignIndex}-${cardIndex}` }}>
            <Layer id={`disclaimer-info-dialog-${campaignIndex}-${cardIndex}`} onDismiss={closeDialog}>
              <InfoDialog
                infoText={offer?.lodgingOffer?.price.disclaimerText}
                closeDialog={closeDialog}
                buttonText={formatText("DestinationsByAffinity.priceDialogCloseButton")}
              />
            </Layer>
          </QueryRoute>
        );

        const cardClicked = mabTrack
          ? () => {
              const mabTrackData: MabTrackEvent = {
                moduleName: "geo_hotels_1",
                eventTarget: "geo_hotels_1",
                eventType: "homepageClick.nonPath",
                rfrr: `MerchCampaign.card-${campaignIndex}-${cardIndex}`,
              };
              mabTrack(mabTrackData);
            }
          : undefined;

        return (
          <UitkCard
            className="HotelInventoryCard uitk-flex-justify-content-flex-start uitk-flex-column"
            withBorder={withBorder}
          >
            <UitkCardMedia>
              <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
                <UitkImage src={offer.image.url} alt={offer.image.description} />
              </UitkFigure>
            </UitkCardMedia>
            <div className="uitk-card-content uitk-cell">
              <UitkCardTextSegment>
                {offer.discountBadge?.text && (
                  <div className="uitk-cell uitk-type-right badgeStyle">
                    <UitkBadge text={offer.discountBadge?.text} className="all-l-margin-three" />
                  </div>
                )}
                <UitkLayoutGrid columns={2} space="two" className="all-b-padding-two">
                  <div>
                    {offer.vipMessage && <div className="uitk-type-200 uitk-type-bold">{offer.vipMessage}</div>}
                    <h3 className="truncate all-b-padding-half pwa-theme--grey-900 uitk-type-heading-500">
                      {offer.name}
                    </h3>
                    <span className="uitk-type-300">{offer.location}</span>
                    {offer.freeCancellationMessage && (
                      <div className="uitk-cell all-t-padding-one uitk-type-300 freeCancellation">
                        {offer.freeCancellationMessage}
                      </div>
                    )}
                    {offer.guestReviewRating && (
                      <div className="uitk-cell all-r-padding-one reviewRating">
                        <span className="uitk-type-heading-300 uitk-cell all-r-padding-one">
                          {offer.guestReviewRating}
                        </span>
                        <span className="uitk-type-300 pwa-theme--grey-700">{offer.guestReviewCount}</span>
                      </div>
                    )}
                  </div>
                  <div className="uitk-cell uitk-type-right">
                    <UitkPriceLockup
                      lockupPrice={offer.lodgingOffer?.price?.value}
                      priceSubtextFirst={offer.lodgingOffer?.price?.qualifierText}
                      priceSubtextStandard={[`${offer?.dates}`]}
                      a11yPrice={a11yPrice}
                      a11yStrikeThroughDialogTrigger={priceDisclaimerLabel}
                      strikeThroughPrice={offer?.lodgingOffer?.strikeThroughPrice?.value}
                      strikeThroughTrigger={
                        <Link
                          to={{
                            pathname: routeStore.pathname,
                            search: routeStore.mergeQueryString(routeStore.search, {
                              pwaDialog: `disclaimer-info-${campaignIndex}-${cardIndex}`,
                            }),
                          }}
                          className="disclaimerInfoIcon"
                        />
                      }
                    />
                    {disclaimerInfoDialog()}
                  </div>
                </UitkLayoutGrid>
              </UitkCardTextSegment>
              <UitkCardLink>
                {offer.lodgingOffer && (
                  <TrackedLink
                    moduleName={moduleName}
                    action={Action.CLICK}
                    href={offer.lodgingOffer?.actionLink.href}
                    rfrr={`card-${campaignIndex}-${cardIndex}`}
                    onClick={cardClicked}
                  >
                    {offer.location}
                  </TrackedLink>
                )}
              </UitkCardLink>
            </div>
          </UitkCard>
        );
      }
    )
  )
);
